import React, { useState, useEffect } from 'react';
import { Scale, ScaleFormData, ScaleType } from '../../types/scale.types';
import { generatePointLabels } from '../../services/scaleService';
import { useScales } from '../../hooks/useScales';
import PageShell from '../../components/layout/PageShell';
import GlassCard from '../../components/ui/GlassCard';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { Input, Textarea, Select } from '../../components/ui/FormFields';

// Clean form structure without unnecessary metadata
const BLANK_FORM: Omit<ScaleFormData, 'createdAt' | 'updatedAt'> = {
  scaleName: '',
  scaleType: 'likert',
  numberOfPoints: 5,
  pointLabels: [],
  reverseScoringAllowed: true,
  description: '',
  isActive: true,
};

const ScalesPage: React.FC = () => {
  const {
    scales,
    loading,
    error,
    filters,
    setFilters,
    createScale,
    updateScale,
    deleteScale,
    toggleStatus,
    bulkDelete,
    refreshScales,
  } = useScales();

  const [showModal, setShowModal] = useState(false);
  const [editScale, setEditScale] = useState<Scale | null>(null);
  const [deleteScaleItem, setDeleteScaleItem] = useState<Scale | null>(null);
  const [form, setForm] = useState(BLANK_FORM);
  const [selectedScales, setSelectedScales] = useState<string[]>([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState<'basic' | 'labels'>('basic');

  // Initialize form with default point labels when modal opens for creation
  useEffect(() => {
    if (!editScale && showModal) {
      setForm({
        ...BLANK_FORM,
        pointLabels: generatePointLabels('likert', 5),
      });
      setActiveTab('basic');
    }
  }, [showModal, editScale]);

  // Reset form when modal closes
  useEffect(() => {
    if (!showModal) {
      setEditScale(null);
      setForm(BLANK_FORM);
    }
  }, [showModal]);

  const updateFormField = <K extends keyof typeof BLANK_FORM>(
    key: K,
    value: (typeof BLANK_FORM)[K]
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleScaleTypeChange = (value: ScaleType) => {
    const newLabels = generatePointLabels(value, form.numberOfPoints);
    updateFormField('scaleType', value);
    updateFormField('pointLabels', newLabels);

    // Disable reverse scoring for incompatible types
    const allowReverse = !['importance', 'forcedChoice'].includes(value);
    updateFormField('reverseScoringAllowed', allowReverse);
  };

  const handleNumberOfPointsChange = (value: number) => {
    const newLabels = generatePointLabels(form.scaleType, value);
    updateFormField('numberOfPoints', value);
    updateFormField('pointLabels', newLabels);
  };

  const updatePointLabel = (index: number, label: string) => {
    const updatedLabels = [...form.pointLabels];
    updatedLabels[index] = { ...updatedLabels[index], label };
    updateFormField('pointLabels', updatedLabels);
  };

  const openNew = () => {
    setEditScale(null);
    setShowModal(true);
  };

  const openEdit = (scale: Scale) => {
    setEditScale(scale);
    setForm({
      scaleName: scale.scaleName,
      scaleType: scale.scaleType,
      numberOfPoints: scale.numberOfPoints,
      pointLabels: scale.pointLabels,
      reverseScoringAllowed: scale.reverseScoringAllowed,
      description: scale.description,
      isActive: scale.isActive,
    });
    setActiveTab('basic');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.scaleName.trim()) {
      alert('Scale name is required');
      return;
    }

    if (form.pointLabels.length === 0) {
      alert('Please configure point labels');
      return;
    }

    try {
      if (editScale) {
        await updateScale(editScale.id, form);
      } else {
        await createScale(form);
      }
      setShowModal(false);
    } catch (err) {
      console.error('Save error:', err);
      alert('Failed to save scale');
    }
  };

  const handleDelete = async () => {
    if (!deleteScaleItem) return;
    try {
      await deleteScale(deleteScaleItem.id);
      setDeleteScaleItem(null);
    } catch (err) {
      alert('Failed to delete scale');
    }
  };

  const handleBulkDelete = async () => {
    try {
      await bulkDelete(selectedScales);
      setSelectedScales([]);
      setShowBulkDeleteConfirm(false);
    } catch (err) {
      alert('Failed to delete scales');
    }
  };

  const toggleSelectAll = () => {
    if (selectedScales.length === scales.length) {
      setSelectedScales([]);
    } else {
      setSelectedScales(scales.map(s => s.id));
    }
  };

  const toggleSelect = (scaleId: string) => {
    setSelectedScales(prev =>
      prev.includes(scaleId)
        ? prev.filter(id => id !== scaleId)
        : [...prev, scaleId]
    );
  };

  const getScaleTypeBadge = (type: string) => {
    const badges: Record<string, string> = {
      likert: 'bg-blue-100 text-blue-800',
      frequency: 'bg-green-100 text-green-800',
      importance: 'bg-yellow-100 text-yellow-800',
      forcedChoice: 'bg-purple-100 text-purple-800',
      sjt: 'bg-red-100 text-red-800',
    };
    return badges[type] || 'bg-gray-100 text-gray-800';
  };

  if (error) {
    return (
      <PageShell title="Scale Library" subtitle="Manage all rating scales">
        <GlassCard className="p-8 text-center">
          <div className="text-red-600 mb-4">{error}</div>
          <Button onClick={refreshScales}>Retry</Button>
        </GlassCard>
      </PageShell>
    );
  }

  return (
    <PageShell
      title="Scale Library"
      subtitle="Centralized Scale Master - Manage all rating scales"
      action={{ label: "Create Scale", onClick: openNew }}
    >
      {/* Statistics Cards - Clean and informative */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <GlassCard className="p-4">
          <div>
            <div className="text-2xl font-bold text-gray-900">{scales.length}</div>
            <div className="text-sm text-gray-500">Total Scales</div>
          </div>
        </GlassCard>
        <GlassCard className="p-4">
          <div>
            <div className="text-2xl font-bold text-green-600">
              {scales.filter(s => s.isActive).length}
            </div>
            <div className="text-sm text-gray-500">Active Scales</div>
          </div>
        </GlassCard>
        <GlassCard className="p-4">
          <div>
            <div className="text-2xl font-bold text-red-600">
              {scales.filter(s => !s.isActive).length}
            </div>
            <div className="text-sm text-gray-500">Inactive Scales</div>
          </div>
        </GlassCard>
        <GlassCard className="p-4">
          <div>
            <div className="text-2xl font-bold text-purple-600">
              {new Set(scales.map(s => s.scaleType)).size}
            </div>
            <div className="text-sm text-gray-500">Scale Types</div>
          </div>
        </GlassCard>
      </div>

      {/* Filters Bar */}
      <GlassCard className="p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Input
            label="Search"
            placeholder="Search by name or description..."
            value={filters.searchTerm || ''}
            onChange={(v) =>
              setFilters((prev) => ({
                ...prev,
                searchTerm: v,
              }))
            }
          />

          <Select
            label="Scale Type"
            value={filters.scaleType || 'all'}
            onChange={(v) =>
              setFilters((prev) => ({
                ...prev,
                scaleType: v as ScaleType | 'all',
              }))
            }
            options={[
              { value: 'all', label: 'All Types' },
              { value: 'likert', label: 'Likert' },
              { value: 'frequency', label: 'Frequency' },
              { value: 'importance', label: 'Importance' },
              { value: 'forcedChoice', label: 'Forced Choice' },
              { value: 'sjt', label: 'SJT' },
            ]}
          />

          <Select
            label="Status"
            value={filters.isActive || 'all'}
            onChange={(v) =>
              setFilters((prev) => ({
                ...prev,
                isActive: v as 'all' | 'true' | 'false',
              }))
            }
            options={[
              { value: 'all', label: 'All Status' },
              { value: 'true', label: 'Active' },
              { value: 'false', label: 'Inactive' },
            ]}
          />

          <div className="flex items-end">
            <Button
              variant="secondary"
              onClick={() => setFilters({ searchTerm: '', scaleType: 'all', isActive: 'all' })}
              className="w-full"
            >
              Clear Filters
            </Button>
          </div>
        </div>
      </GlassCard>

      {/* Bulk Actions Bar */}
      {selectedScales.length > 0 && (
        <div className="mb-4 p-3 bg-indigo-50 rounded-lg flex justify-between items-center">
          <span className="text-sm font-medium text-indigo-800">
            {selectedScales.length} scale(s) selected
          </span>
          <Button
            variant="danger"
            size="sm"
            onClick={() => setShowBulkDeleteConfirm(true)}
          >
            Delete Selected
          </Button>
        </div>
      )}

      {/* Scales Grid */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      ) : scales.length === 0 ? (
        <GlassCard className="p-12 text-center">
          <h3 className="text-xl font-bold text-gray-700 mb-2">No Scales Found</h3>
          <p className="text-gray-500 mb-4">
            {filters.searchTerm || filters.scaleType !== 'all' || filters.isActive !== 'all'
              ? 'Try adjusting your filters'
              : 'Create your first scale to get started'}
          </p>
          <Button onClick={openNew}>Create Your First Scale</Button>
        </GlassCard>
      ) : (
        <>
          {/* Select All Toggle */}
          <div className="mb-4 flex justify-end">
            <button
              onClick={toggleSelectAll}
              className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
            >
              {selectedScales.length === scales.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {scales.map((scale) => (
              <GlassCard key={scale.id} className="p-5 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={selectedScales.includes(scale.id)}
                    onChange={() => toggleSelect(scale.id)}
                    className="mt-1 w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${getScaleTypeBadge(scale.scaleType)}`}>
                        {scale.scaleType.toUpperCase()}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${scale.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {scale.isActive ? 'Active' : 'Inactive'}
                      </span>
                      {scale.reverseScoringAllowed && (
                        <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full font-medium">
                          Reverse Scoring
                        </span>
                      )}
                    </div>

                    <h3 className="font-bold text-gray-900 text-lg">{scale.scaleName}</h3>
                    {scale.description && (
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2">{scale.description}</p>
                    )}

                    <div className="mt-3 bg-gray-50 rounded-lg p-3">
                      <div className="text-xs font-semibold text-gray-600 mb-2">
                        {scale.numberOfPoints}-Point Scale
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {scale.pointLabels.slice(0, 3).map((pl) => (
                          <span key={pl.point} className="text-xs bg-white px-2 py-1 rounded border">
                            {pl.point}: {pl.label}
                          </span>
                        ))}
                        {scale.pointLabels.length > 3 && (
                          <span className="text-xs text-gray-400">
                            +{scale.pointLabels.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2 mt-4">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(scale)}>
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => toggleStatus(scale.id, !scale.isActive)}
                      >
                        {scale.isActive ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setDeleteScaleItem(scale)}>
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </GlassCard>
            ))}
          </div>
        </>
      )}

      {/* Create/Edit Modal - No inner scrollbars now */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editScale ? "Edit Scale" : "Create New Scale"}
        size="lg"
      >
        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200 mb-6 sticky top-0 bg-white z-10" style={{ borderRadius: '24px' }}>
          <button
            onClick={() => setActiveTab('basic')}
            className={`px-4 py-2 font-medium text-sm rounded-t-lg transition-colors ${activeTab === 'basic'
                ? 'text-indigo-600 border-b-2 border-indigo-600'
                : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            Basic Information
          </button>
          <button
            onClick={() => setActiveTab('labels')}
            className={`px-4 py-2 font-medium text-sm rounded-t-lg transition-colors ${activeTab === 'labels'
                ? 'text-indigo-600 border-b-2 border-indigo-600'
                : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            Point Labels ({form.pointLabels.length})
          </button>
        </div>

        {/* Tab Content - No extra scrollbars here */}
        <div>
          {activeTab === 'basic' && (
            <div className="space-y-4">
              <Input
                label="Scale Name"
                value={form.scaleName}
                onChange={(v) => updateFormField('scaleName', v)}
                placeholder="e.g., Employee Satisfaction Scale"
                required
                labelClassName="text-gray-900"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Scale Type"
                  value={form.scaleType}
                  onChange={(v) => handleScaleTypeChange(v as ScaleType)}
                  options={[
                    { value: 'likert', label: 'Likert (Agreement)' },
                    { value: 'frequency', label: 'Frequency' },
                    { value: 'importance', label: 'Importance' },
                    { value: 'forcedChoice', label: 'Forced Choice' },
                    { value: 'sjt', label: 'SJT (Situational Judgment)' },
                  ]}
                  required
                />

                <Input
                  label="Number of Points"
                  type="number"
                  value={form.numberOfPoints}
                  onChange={(v) => handleNumberOfPointsChange(parseInt(v))}
                  required
                />
              </div>

              <Textarea
                label="Description"
                value={form.description}
                onChange={(v) => updateFormField('description', v)}
                placeholder="Describe when and how to use this scale..."
                rows={3}
              />

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-700 mb-2">Preview</h4>
                <div className="text-gray-900">
                  <span className="font-semibold">
                    {form.scaleName || 'Scale Name'}
                  </span>
                  <span className="text-gray-400 text-sm ml-2">
                    ({form.numberOfPoints}-point {form.scaleType} scale)
                  </span>
                </div>
                {form.description && (
                  <p className="text-sm text-gray-500 mt-1">
                    {form.description}
                  </p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'labels' && (
            <div className="space-y-3">
              <div className="bg-blue-50 p-3 rounded-lg mb-4">
                <p className="text-sm text-blue-800">
                  Customize the label and value for each point on your scale.
                </p>
              </div>

              <div className="space-y-3">
                {form.pointLabels.map((pl, idx) => (
                  <div
                    key={pl.point}
                    className="bg-white border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-start gap-4 mb-3">
                      <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center font-bold text-indigo-700 mt-6">
                        {pl.point}
                      </div>

                      <div className="flex-1">
                        <Input
                          label={`Point ${pl.point} Label`}
                          value={pl.label}
                          onChange={(v) => updatePointLabel(idx, v)}
                          placeholder={`Label for point ${pl.point}`}
                          labelClassName="text-gray-800"
                        />
                      </div>
                    </div>

                    <div className="ml-14">
                      <Input
                        label="Value"
                        type="number"
                        value={pl.value}
                        onChange={(v) => {
                          const updatedLabels = [...form.pointLabels];
                          updatedLabels[idx] = {
                            ...updatedLabels[idx],
                            value: parseInt(v),
                          };
                          updateFormField('pointLabels', updatedLabels);
                        }}
                        inputClassName="w-32"
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        used in scoring
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Advanced Settings */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">Allow Reverse Scoring</label>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.reverseScoringAllowed}
                  onChange={(e) => updateFormField('reverseScoringAllowed', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">Active Status</label>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => updateFormField('isActive', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
              </label>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex gap-3 justify-end pt-6 mt-4 border-t border-gray-200">
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            {editScale ? 'Update Scale' : 'Create Scale'}
          </Button>
        </div>
      </Modal>

      {/* Delete Confirm Dialog */}
      <ConfirmDialog
        isOpen={!!deleteScaleItem}
        onClose={() => setDeleteScaleItem(null)}
        onConfirm={handleDelete}
        title="Delete Scale"
        message={`Are you sure you want to delete "${deleteScaleItem?.scaleName}"? This action cannot be undone and will affect all questions using this scale.`}
      />

      {/* Bulk Delete Confirm Dialog */}
      <ConfirmDialog
        isOpen={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        onConfirm={handleBulkDelete}
        title="Bulk Delete Scales"
        message={`Are you sure you want to delete ${selectedScales.length} scale(s)? This action cannot be undone.`}
      />
    </PageShell>
  );
};

export default ScalesPage;